import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';

import { IconComponent } from '../icon/icon';
import { CardFoldService } from './card-fold.service';

/**
 * A panel whose body is hidden until asked for, with the gist in the header.
 *
 * **The summary is the point, not the chevron.** A collapsed panel that says
 * only its title makes the reader open it to find out whether it mattered,
 * which is the scroll it was meant to save. So `summary` carries what the body
 * would have told them at a glance — "7 documents · all look good", "ZAR 3.01"
 * — and opening it is for the detail behind that.
 *
 * FLOW-402 (DOC-89-A §4): on the Ops application pages every section is one
 * of these, folded on a person's first visit (the four always-open sections
 * are plain cards, not this). **The summary is the record's**, supplied by the
 * back end in its own words — an act, a hold or a refusal named, no colour
 * carrying it — and the screen computes none of it. With `memory` set, the
 * card remembers whether this person left it open on this application
 * (`CardFoldService`); with `anchors`, a link to an id inside it opens it.
 *
 * ⚠️ **Only for material that is reference, not decision.** Anything an Ops
 * user needs in order to decide belongs on the page unfolded; hiding it behind
 * a click does not make the screen simpler, it makes the decision worse.
 */
@Component({
  selector: 'app-collapsible-panel',
  imports: [MatCardModule, IconComponent],
  template: `
    <mat-card appearance="outlined" class="sg-panel collapsible" [attr.id]="anchorId()">
      <h2 class="collapsible__heading">
        <button
          type="button"
          class="collapsible__head"
          [attr.aria-expanded]="isOpen()"
          [attr.data-testid]="'collapsible-toggle-' + testId()"
          (click)="toggle()"
        >
          <app-icon [name]="isOpen() ? 'chevron-down' : 'chevron-right'" [size]="18" />
          <span class="collapsible__title">{{ title() }}</span>
          @if (summary()) {
            <span
              class="collapsible__summary"
              [attr.data-testid]="'collapsible-summary-' + testId()"
              >{{ summary() }}</span
            >
          }
        </button>
      </h2>

      @if (isOpen()) {
        <div class="collapsible__body" [attr.data-testid]="'collapsible-body-' + testId()">
          <ng-content />
        </div>
      }
    </mat-card>
  `,
  styleUrl: './collapsible-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollapsiblePanelComponent {
  readonly title = input.required<string>();
  /** The gist, shown while closed — what the body would have said at a glance. */
  readonly summary = input<string>('');
  /** Suffix for the test ids, so a spec can address one panel of several. */
  readonly testId = input.required<string>();
  /** Panels that start open: use for anything a reviewer reads most visits. */
  readonly startOpen = input(false);
  /**
   * FLOW-402: remember this card per person per application. The scope names
   * the application (`KS_APPLICATION:<short_id>`); the service adds the user.
   */
  readonly memory = input<string | null>(null);
  /** Ids inside the body that an in-page link may point at; the card opens for them. */
  readonly anchors = input<readonly string[]>([]);
  /** An id for the card itself, so a link can point at the card as a whole. */
  readonly anchorId = input<string | null>(null);

  private readonly fold = inject(CardFoldService);
  private readonly toggled = signal<boolean | null>(null);
  private readonly remembered = computed(() => {
    const scope = this.memory();
    return scope ? this.fold.isOpen(scope, this.testId()) : null;
  });
  protected readonly isOpen = computed(
    () => this.toggled() ?? this.remembered() ?? this.startOpen(),
  );

  constructor() {
    const unregister = this.fold.register({
      anchors: () => [...this.anchors(), ...(this.anchorId() ? [this.anchorId() ?? ''] : [])],
      open: () => this.setOpen(true),
    });
    inject(DestroyRef).onDestroy(unregister);
  }

  protected toggle(): void {
    this.setOpen(!this.isOpen());
  }

  private setOpen(open: boolean): void {
    this.toggled.set(open);
    const scope = this.memory();
    if (scope) this.fold.remember(scope, this.testId(), open);
  }
}
